/**
 * Timestamp Type Detector
 * 
 * Detects various timestamp formats (Unix, ISO 8601, etc.).
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const TimestampDetector: ITypeDetector = {
  id: 'timestamp-detector',
  name: 'Timestamp Detector',
  priority: 88, // High priority for exact matches
  detectedType: SEMANTIC_TYPES.TIMESTAMP,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    // Unix timestamp (seconds): 10 digits, reasonable range
    if (/^\d{10}$/.test(trimmed)) {
      const ts = parseInt(trimmed, 10);
      // Reasonable range: 1970-01-01 to 2100-01-01
      if (ts >= 0 && ts <= 4102444800) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    // Unix timestamp (milliseconds): 13 digits
    if (/^\d{13}$/.test(trimmed)) {
      const ts = parseInt(trimmed, 10);
      // Reasonable range in milliseconds
      if (ts >= 0 && ts <= 4102444800000) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    // ISO 8601 format: 2024-12-11T10:30:00Z or 2024-12-11T10:30:00.000Z
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:?\d{2})?$/i.test(trimmed)) {
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    // ISO 8601 date only: 2024-12-11
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    // RFC 2822: Wed, 11 Dec 2024 10:30:00 GMT
    if (/^[A-Za-z]{3},\s+\d{1,2}\s+[A-Za-z]{3}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s+[A-Z]{3}$/i.test(trimmed)) {
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    // Common formats: 12/11/2024, 2024/12/11, 11-Dec-2024
    const commonDatePatterns = [
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/, // MM/DD/YYYY or DD/MM/YYYY
      /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/MM/DD
      /^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/i, // DD-Mon-YYYY
    ];

    for (const pattern of commonDatePatterns) {
      if (pattern.test(trimmed)) {
        return SEMANTIC_TYPES.TIMESTAMP;
      }
    }

    return null;
  },
};
